import React, { useState, useEffect } from 'react';
import { X, Edit2, Trash2, Plus, Search, ChevronDown, ChevronUp, GraduationCap, BookOpen, Users, HelpCircle, Tag, GripVertical, FileText } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const ProgramForm = ({ mode = 'new', program = null, paths = [], groups = [], questions = [], tags = [], submissionForms = [], onClose, onSubmit, onEdit, onDelete, programs = [] }) => {
  const [title, setTitle] = useState('');
  const [includedPaths, setIncludedPaths] = useState([]);
  const [availablePaths, setAvailablePaths] = useState([]);
  const [includedGroups, setIncludedGroups] = useState([]);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [includedQuestions, setIncludedQuestions] = useState([]);
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [includedSubmissionForms, setIncludedSubmissionForms] = useState([]);
  const [availableSubmissionForms, setAvailableSubmissionForms] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [leaderboardMetric, setLeaderboardMetric] = useState('xp');
  const [primaryColor, setPrimaryColor] = useState('#1a9a84');
  const [referralsEnabled, setReferralsEnabled] = useState(false);
  const [searchQuery, setSearchQuery] = useState({ paths: '', groups: '', questions: '', submissionForms: '' });
  const [searchContext, setSearchContext] = useState({ 
    paths: 'all',
    groups: 'all',
    questions: 'all',
    submissionForms: 'all'
  });
  const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState({
    paths: false,
    groups: false,
    questions: false,
    submissionForms: false
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedSection, setExpandedSection] = useState('paths'); // 'paths', 'groups', 'questions'

  // Setup sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    setIsLoading(true);
    try {
      if (mode === 'edit' || mode === 'view') {
        setTitle(program?.title || '');
        
        // Handle paths
        const getIdStr = (id) => typeof id === 'object' && id !== null ? String(id._id) : String(id);
        const includedP = paths.filter(p => program?.paths?.some(id => String(p._id) === getIdStr(id)));
        setIncludedPaths(includedP);
        const availableP = paths.filter(p => !program?.paths?.some(id => String(p._id) === getIdStr(id)));
        setAvailablePaths(availableP);
        
        // Handle groups
        const includedG = groups.filter(g => program?.groups?.some(id => (g._id === id || (typeof id === 'object' && g._id === id._id))));
        setIncludedGroups(includedG);
        const availableG = groups.filter(g => !program?.groups?.some(id => (g._id === id || (typeof id === 'object' && g._id === id._id))));
        setAvailableGroups(availableG);

        // Handle questions - preserve order from database
        const includedQ = [];
        if (program?.questions) {
          // Map through program.questions to preserve database order
          program.questions.forEach(programQuestion => {
            const questionId = typeof programQuestion === 'object' ? programQuestion._id : programQuestion;
            const foundQuestion = questions.find(q => q._id === questionId);
            if (foundQuestion) {
              includedQ.push(foundQuestion);
            }
          });
        }
        setIncludedQuestions(includedQ);
        const availableQ = questions.filter(q => !program?.questions?.some(id => (q._id === id || (typeof id === 'object' && q._id === id._id))));
        setAvailableQuestions(availableQ);

        // Handle submission forms - all forms can be assigned to programs
        const includedSF = submissionForms.filter(sf =>
          program?.submissionForms?.some(id => (sf._id === id || (typeof id === 'object' && sf._id === id._id)))
        );
        setIncludedSubmissionForms(includedSF);
        const availableSF = submissionForms.filter(sf =>
          !program?.submissionForms?.some(id => (sf._id === id || (typeof id === 'object' && sf._id === id._id)))
        );
        setAvailableSubmissionForms(availableSF);

        // Handle single tag
        setSelectedTag(program?.tag || null);
        setLeaderboardMetric(program?.leaderboardMetric || 'xp');
        setPrimaryColor(program?.primaryColor || '#1a9a84');
        setReferralsEnabled(program?.referralsEnabled || false);
      } else {
        setTitle('');
        setIncludedPaths([]);
        setAvailablePaths(paths);
        setIncludedGroups([]);
        setAvailableGroups(groups);
        setIncludedQuestions([]);
        setAvailableQuestions(questions);
        setIncludedSubmissionForms([]);
        setAvailableSubmissionForms(submissionForms);
        setSelectedTag(null);
        setLeaderboardMetric('xp');
        setPrimaryColor('#1a9a84');
        setReferralsEnabled(false);
      }
      setErrors({});
      setSearchQuery({ paths: '', groups: '', questions: '', submissionForms: '' });
      setShowSuccess(false);
    } finally {
      setIsLoading(false);
    }
  }, [mode, program, paths, groups, questions, tags, submissionForms]);

  const isList = mode === 'list';
  const isView = mode === 'view';
  const isEdit = mode === 'edit';
  const isNew = mode === 'new';

  // Sortable Question Item Component
  const SortableQuestionItem = ({ question, onRemove }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: question._id, disabled: isView || isSubmitting });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`flex items-center p-2 border-b border-[#1086a4] last:border-b-0 transition-all ${
          isDragging
            ? 'bg-white shadow-lg border-2 border-[#1086a4] rounded-md z-50 opacity-50'
            : 'hover:bg-[#f9fafb]'
        }`}
      >
        {!isView && (
          <div
            {...listeners}
            {...attributes}
            className="mr-3 p-1 text-gray-400 hover:text-[#1086a4] cursor-grab active:cursor-grabbing"
          >
            <GripVertical className="h-4 w-4" />
          </div>
        )}
        <span className="text-sm text-gray-900 flex-1">{question.text}</span>
        {!isView && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(question._id);
            }}
            className="ml-2 text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 transition-colors"
            style={{ touchAction: 'none' }}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const newErrors = {};
    if (!title.trim()) {
      newErrors.title = 'Program title is required';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setErrors({});
    setIsSubmitting(true);
    
    try {
      const questionIds = includedQuestions.map(q => q._id);
      await onSubmit({ 
        title: title.trim(), 
        paths: includedPaths.map(p => p._id),
        groups: includedGroups.map(g => g._id),
        questions: questionIds,
        submissionForms: includedSubmissionForms.map(sf => sf._id),
        tag: selectedTag?._id || null,
        leaderboardMetric,
        primaryColor,
        referralsEnabled
      });
      
      setShowSuccess(true);
      setTimeout(() => {
        if (isNew) {
          setTitle('');
          setIncludedPaths([]);
          setAvailablePaths(paths);
          setIncludedGroups([]);
          setAvailableGroups(groups);
          setIncludedQuestions([]);
          setAvailableQuestions(questions);
          setIncludedSubmissionForms([]);
          setAvailableSubmissionForms(submissionForms);
          setSelectedTag(null);
          setLeaderboardMetric('xp');
          setPrimaryColor('#1a9a84');
          setReferralsEnabled(false);
          setShowSuccess(false);
        }
        onClose();
      }, 1500);
    } catch (error) {
      setErrors({ submit: 'Failed to save program. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSearchPlaceholder = (section) => {
    switch (searchContext[section]) {
      case 'included':
        return `Search in selected ${section}...`;
      case 'available':
        return `Search in available ${section}...`;
      default:
        return `Search all ${section}...`;
    }
  };

  const handleSearchContextChange = (section, context) => {
    setSearchContext(prev => ({ ...prev, [section]: context }));
    setIsSearchDropdownOpen(prev => ({ ...prev, [section]: false }));
  };

  const toggleSearchDropdown = (section) => {
    setIsSearchDropdownOpen(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Update filter functions
  const getFilteredItems = (items, section, included = false) => {
    if (!searchQuery[section]) return items;
    
    if (searchContext[section] === 'all' || 
        (included && searchContext[section] === 'included') ||
        (!included && searchContext[section] === 'available')) {
      return items.filter(item => 
        (item.title || item.text || '').toLowerCase().includes(searchQuery[section].toLowerCase())
      );
    }
    
    return items;
  };

  const handleQuestionDragEnd = (event) => {
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      const oldIndex = includedQuestions.findIndex((q) => q._id === active.id);
      const newIndex = includedQuestions.findIndex((q) => q._id === over.id);

      const reorderedQuestions = arrayMove(includedQuestions, oldIndex, newIndex);
      setIncludedQuestions(reorderedQuestions);
    }
  };

  if (isList) {
    return (
      <div className="mt-8">
        {/* Header and Buttons Section */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <h2 className="text-2xl font-bold text-[#054581]">Programs Management</h2>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
            <button
              className="bg-[#1086a4] text-white px-4 sm:px-6 py-2 rounded-lg font-semibold shadow hover:bg-[#054581] transition-colors flex items-center justify-center"
              onClick={() => onEdit(null)}
            >
              <Plus className="w-5 h-5 mr-2" />
              Add new program
            </button>
          </div>
        </div>

        {/* Mobile View */}
        <div className="md:hidden space-y-4">
          {programs.map(program => (
            <div key={program._id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4">
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className="w-10 h-10 rounded-full bg-[#1086a4] bg-opacity-10 flex items-center justify-center flex-shrink-0">
                    <GraduationCap className="w-5 h-5 text-[#1086a4]" />
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-[#054581] mb-2">{program.title}</h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <BookOpen className="w-4 h-4 text-[#1086a4]" />
                        <span className="text-[#1e2d40]">
                          {program.paths?.length || 0} Learning Paths
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Users className="w-4 h-4 text-[#1086a4]" />
                        <span className="text-[#1e2d40]">
                          {program.groups?.length || 0} Groups
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <HelpCircle className="w-4 h-4 text-[#1086a4]" />
                        <span className="text-[#1e2d40]">
                          {program.questions?.length || 0} Questions
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <FileText className="w-4 h-4 text-[#1086a4]" />
                        <span className="text-[#1e2d40]">
                          {program.submissionForms?.length || 0} Repeatable Submission Forms
                        </span>
                      </div>
                      {program.tag && (
                        <div className="flex items-center gap-2 text-sm">
                          <Tag className="w-4 h-4 text-[#1086a4]" />
                          <span className="text-[#1e2d40]">
                            {program.tag.title}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Bar */}
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-2">
                <button
                  onClick={() => onEdit(program)}
                  className="p-2 text-[#1086a4] hover:text-[#054581] transition-colors rounded-lg hover:bg-white"
                >
                  <Edit2 size={18} />
                </button>
                <button
                  onClick={() => onDelete(program)}
                  className="p-2 text-red-500 hover:text-red-700 transition-colors rounded-lg hover:bg-white"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop View */}
        <div className="hidden md:block w-full overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#054581] uppercase tracking-wider">
                  Program Title
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#054581] uppercase tracking-wider">
                  Paths
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#054581] uppercase tracking-wider">
                  Groups
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#054581] uppercase tracking-wider">
                  Questions
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#054581] uppercase tracking-wider">
                  Repeatable Submission Forms
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#054581] uppercase tracking-wider">
                  Tag
                </th>
                <th scope="col" className="relative px-6 py-3 w-24">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {programs.map(program => (
                <tr key={program._id} className="hover:bg-gray-50 transition-colors duration-150">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{program.title}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {program.paths ? program.paths.length : 0} paths
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {program.groups ? program.groups.length : 0} groups
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {program.questions ? program.questions.length : 0} questions
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {program.submissionForms ? program.submissionForms.length : 0} forms
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {program.tag ? program.tag.title : '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-3">
                      <button
                        onClick={() => onEdit(program)}
                        className="text-[#1086a4] hover:text-[#054581] transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onDelete(program)}
                        className="text-red-500 hover:text-red-700 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Modal Form View
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <form
        className="bg-white rounded-lg shadow-lg w-full relative border-2 border-[#1086a4] max-h-[90vh] overflow-y-auto sm:max-w-lg md:max-w-2xl"
        onSubmit={handleSubmit}
      >
        {/* Header */}
        <div className="p-6 border-b border-[#1086a4]">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-[#054581]">
              {isNew && 'Add New Program'}
              {isEdit && 'Edit Program'}
              {isView && 'View Program'}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-[#1086a4] focus:outline-none transition-colors"
              disabled={isSubmitting}
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Success Message */}
        {showSuccess && (
          <div className="p-4 bg-green-50 border-b border-green-200">
            <p className="text-green-700 text-sm font-medium">
              Program {isNew ? 'created' : 'updated'} successfully!
            </p>
          </div>
        )}

        {/* Error Message */}
        {errors.submit && (
          <div className="p-4 bg-red-50 border-b border-red-200">
            <p className="text-red-700 text-sm font-medium">{errors.submit}</p>
          </div>
        )}

        {/* Form Content */}
        <div className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1086a4]"></div>
            </div>
          ) : (
            <>
              {/* Title Field */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#054581] mb-2">
                  Program Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className={`w-full px-3 py-2 bg-[#f9fafb] border rounded-md text-gray-900 placeholder-gray-500 ${
                    errors.title 
                      ? 'border-red-500 focus:border-red-400' 
                      : 'border-[#1086a4] focus:border-[#1086a4]'
                  } focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors`}
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  required
                  disabled={isView || isSubmitting}
                  placeholder="Enter program title"
                />
                {errors.title && (
                  <p className="text-red-500 text-sm mt-1">{errors.title}</p>
                )}
              </div>

              {/* Tag Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#054581] mb-2">
                  Tag (Optional)
                </label>
                <select
                  value={selectedTag?._id || ''}
                  onChange={(e) => {
                    const tagId = e.target.value;
                    const tag = tagId ? tags.find(t => t._id === tagId) : null;
                    setSelectedTag(tag);
                  }}
                  className="w-full px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors"
                  disabled={isView || isSubmitting}
                >
                  <option value="">No tag selected</option>
                  {tags.map(tag => (
                    <option key={tag._id} value={tag._id}>
                      {tag.title}
                    </option>
                  ))}
                </select>
              </div>

              {/* Leaderboard Metric */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#054581] mb-2">
                  Leaderboard Metric
                </label>
                <select
                  value={leaderboardMetric}
                  onChange={(e) => setLeaderboardMetric(e.target.value)}
                  className="w-full px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors"
                  disabled={isView || isSubmitting}
                >
                  <option value="xp">Most XP</option>
                  <option value="badges">Most Badges</option>
                  <option value="lessons">Most Lessons</option>
                  <option value="courses">Most Missions</option>
                </select>
                <p className="text-gray-400 text-xs mt-1">Controls how users are ranked on the leaderboard for this program</p>
              </div>

              {/* Primary Color */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#054581] mb-2">
                  Program Color
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    disabled={isView || isSubmitting}
                    className="w-10 h-10 rounded cursor-pointer border border-[#1086a4] p-0.5"
                  />
                  <input
                    type="text"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    disabled={isView || isSubmitting}
                    className="w-28 px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors"
                    placeholder="#1a9a84"
                  />
                  <div
                    className="h-10 flex-1 rounded-md border border-gray-200"
                    style={{ backgroundColor: primaryColor }}
                  />
                </div>
                <p className="text-gray-400 text-xs mt-1">Sets the accent color for buttons and progress bars on the student dashboard</p>
              </div>

              {/* Referrals Toggle */}
              <div className="mb-6">
                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <span className="text-sm font-medium text-[#054581]">Enable Referrals</span>
                    <p className="text-gray-400 text-xs mt-1">Allow students in this program to refer friends</p>
                  </div>
                  <div className="relative">
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={referralsEnabled}
                      onChange={(e) => setReferralsEnabled(e.target.checked)}
                      disabled={isView || isSubmitting}
                    />
                    <div className={`w-11 h-6 rounded-full transition-colors ${referralsEnabled ? 'bg-[#1086a4]' : 'bg-gray-300'} ${(isView || isSubmitting) ? 'opacity-50' : ''}`}>
                      <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${referralsEnabled ? 'translate-x-5' : ''}`} />
                    </div>
                  </div>
                </label>
              </div>

              {/* Collapsible Sections */}
              <div className="space-y-4">
                {/* Paths Section */}
                <div className="border border-[#1086a4] rounded-md overflow-hidden">
                  <button
                    type="button"
                    className="w-full px-4 py-3 bg-[#f9fafb] flex items-center justify-between text-[#054581] hover:bg-gray-50 transition-colors"
                    onClick={() => setExpandedSection(expandedSection === 'paths' ? null : 'paths')}
                  >
                    <span className="font-medium">Paths ({includedPaths.length})</span>
                    {expandedSection === 'paths' ? (
                      <ChevronUp className="h-5 w-5" />
                    ) : (
                      <ChevronDown className="h-5 w-5" />
                    )}
                  </button>
                  
                  {expandedSection === 'paths' && (
                    <div className="p-4">
                      <div className="mb-4">
                        <div className="relative flex">
                          {/* Search Context Dropdown Button */}
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() => toggleSearchDropdown('paths')}
                              className="h-full px-3 py-2 bg-[#1086a4] text-white rounded-l-md border-r border-white/20 hover:bg-[#054581] transition-colors flex items-center gap-1 focus:outline-none focus:ring-2 focus:ring-[#1086a4]"
                              disabled={isView || isSubmitting}
                            >
                              <Search className="h-5 w-5" />
                              <ChevronDown className="h-4 w-4" />
                            </button>
                            
                            {/* Dropdown Menu */}
                            {isSearchDropdownOpen.paths && (
                              <div className="absolute z-50 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200">
                                <div className="py-1">
                                  <button
                                    type="button"
                                    onClick={() => handleSearchContextChange('paths', 'all')}
                                    className={`w-full text-left px-4 py-2 text-sm ${
                                      searchContext.paths === 'all'
                                        ? 'bg-[#1086a4] text-white'
                                        : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                                  >
                                    All Paths
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleSearchContextChange('paths', 'included')}
                                    className={`w-full text-left px-4 py-2 text-sm ${
                                      searchContext.paths === 'included'
                                        ? 'bg-[#1086a4] text-white'
                                        : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                                  >
                                    Selected Paths
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleSearchContextChange('paths', 'available')}
                                    className={`w-full text-left px-4 py-2 text-sm ${
                                      searchContext.paths === 'available'
                                        ? 'bg-[#1086a4] text-white'
                                        : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                                  >
                                    Available Paths
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Search Input */}
                          <div className="flex-1 relative">
                            <input
                              type="text"
                              className="w-full pl-3 pr-10 py-2 border border-l-0 border-[#1086a4] rounded-r-md bg-[#f9fafb] text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors"
                              placeholder={getSearchPlaceholder('paths')}
                              value={searchQuery.paths}
                              onChange={(e) => setSearchQuery({ ...searchQuery, paths: e.target.value })}
                              disabled={isView || isSubmitting}
                            />
                            {searchQuery.paths && (
                              <button
                                type="button"
                                onClick={() => setSearchQuery(prev => ({ ...prev, paths: '' }))}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="space-y-4">
                        {/* Selected Paths */}
                        <div className={searchContext.paths === 'available' ? 'opacity-50' : ''}>
                          <h4 className="text-sm font-medium text-[#054581] mb-2">Selected Paths</h4>
                          <div className="border border-[#1086a4] rounded-md divide-y divide-[#1086a4] max-h-[200px] overflow-y-auto">
                            {includedPaths.length > 0 ? (
                              getFilteredItems(includedPaths, 'paths', true).map(path => (
                                  <div
                                    key={path._id}
                                    className="flex items-center justify-between p-2 hover:bg-[#f9fafb] transition-colors"
                                  >
                                    <span className="text-sm text-gray-900">{path.title}</span>
                                    {!isView && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setIncludedPaths(includedPaths.filter(p => p._id !== path._id));
                                          setAvailablePaths([...availablePaths, path]);
                                        }}
                                        className="ml-2 text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 transition-colors"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    )}
                                  </div>
                                ))
                            ) : (
                              <div className="p-4 text-center text-gray-500 text-sm">
                                No paths selected
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Available Paths */}
                        <div className={searchContext.paths === 'included' ? 'opacity-50' : ''}>
                          <h4 className="text-sm font-medium text-[#054581] mb-2">Available Paths</h4>
                          <div className="border border-[#1086a4] rounded-md divide-y divide-[#1086a4] max-h-[200px] overflow-y-auto">
                            {availablePaths.length > 0 ? (
                              getFilteredItems(availablePaths, 'paths', false).map(path => (
                                  <div
                                    key={path._id}
                                    className="flex items-center justify-between p-2 hover:bg-[#f9fafb] transition-colors"
                                  >
                                    <span className="text-sm text-gray-900">{path.title}</span>
                                    {!isView && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setIncludedPaths([...includedPaths, path]);
                                          setAvailablePaths(availablePaths.filter(p => p._id !== path._id));
                                        }}
                                        className="ml-2 text-green-500 hover:text-green-700 p-1 rounded-full hover:bg-green-50 transition-colors"
                                      >
                                        <Plus className="h-4 w-4" />
                                      </button>
                                    )}
                                  </div>
                                ))
                            ) : (
                              <div className="p-4 text-center text-gray-500 text-sm">
                                No paths available
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Groups Section - Similar structure to Paths */}
                <div className="border border-[#1086a4] rounded-md overflow-hidden">
                  <button
                    type="button"
                    className="w-full px-4 py-3 bg-[#f9fafb] flex items-center justify-between text-[#054581] hover:bg-gray-50 transition-colors"
                    onClick={() => setExpandedSection(expandedSection === 'groups' ? null : 'groups')}
                  >
                    <span className="font-medium">Groups ({includedGroups.length})</span>
                    {expandedSection === 'groups' ? (
                      <ChevronUp className="h-5 w-5" />
                    ) : (
                      <ChevronDown className="h-5 w-5" />
                    )}
                  </button>
                  
                  {expandedSection === 'groups' && (
                    <div className="p-4">
                      <div className="mb-4">
                        <div className="relative flex">
                          {/* Search Context Dropdown Button */}
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() => toggleSearchDropdown('groups')}
                              className="h-full px-3 py-2 bg-[#1086a4] text-white rounded-l-md border-r border-white/20 hover:bg-[#054581] transition-colors flex items-center gap-1 focus:outline-none focus:ring-2 focus:ring-[#1086a4]"
                              disabled={isView || isSubmitting}
                            >
                              <Search className="h-5 w-5" />
                              <ChevronDown className="h-4 w-4" />
                            </button>
                            
                            {/* Dropdown Menu */}
                            {isSearchDropdownOpen.groups && (
                              <div className="absolute z-50 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200">
                                <div className="py-1">
                                  <button
                                    type="button"
                                    onClick={() => handleSearchContextChange('groups', 'all')}
                                    className={`w-full text-left px-4 py-2 text-sm ${
                                      searchContext.groups === 'all'
                                        ? 'bg-[#1086a4] text-white'
                                        : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                                  >
                                    All Groups
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleSearchContextChange('groups', 'included')}
                                    className={`w-full text-left px-4 py-2 text-sm ${
                                      searchContext.groups === 'included'
                                        ? 'bg-[#1086a4] text-white'
                                        : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                                  >
                                    Selected Groups
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleSearchContextChange('groups', 'available')}
                                    className={`w-full text-left px-4 py-2 text-sm ${
                                      searchContext.groups === 'available'
                                        ? 'bg-[#1086a4] text-white'
                                        : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                                  >
                                    Available Groups
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Search Input */}
                          <div className="flex-1 relative">
                            <input
                              type="text"
                              className="w-full pl-3 pr-10 py-2 border border-l-0 border-[#1086a4] rounded-r-md bg-[#f9fafb] text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors"
                              placeholder={getSearchPlaceholder('groups')}
                              value={searchQuery.groups}
                              onChange={(e) => setSearchQuery({ ...searchQuery, groups: e.target.value })}
                              disabled={isView || isSubmitting}
                            />
                            {searchQuery.groups && (
                              <button
                                type="button"
                                onClick={() => setSearchQuery(prev => ({ ...prev, groups: '' }))}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="space-y-4">
                        {/* Selected Groups */}
                        <div className={searchContext.groups === 'available' ? 'opacity-50' : ''}>
                          <h4 className="text-sm font-medium text-[#054581] mb-2">Selected Groups</h4>
                          <div className="border border-[#1086a4] rounded-md divide-y divide-[#1086a4] max-h-[200px] overflow-y-auto">
                            {includedGroups.length > 0 ? (
                              getFilteredItems(includedGroups, 'groups', true).map(group => (
                                  <div
                                    key={group._id}
                                    className="flex items-center justify-between p-2 hover:bg-[#f9fafb] transition-colors"
                                  >
                                    <span className="text-sm text-gray-900">{group.title}</span>
                                    {!isView && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setIncludedGroups(includedGroups.filter(g => g._id !== group._id));
                                          setAvailableGroups([...availableGroups, group]);
                                        }}
                                        className="ml-2 text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 transition-colors"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    )}
                                  </div>
                                ))
                            ) : (
                              <div className="p-4 text-center text-gray-500 text-sm">
                                No groups selected
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Available Groups */}
                        <div className={searchContext.groups === 'included' ? 'opacity-50' : ''}>
                          <h4 className="text-sm font-medium text-[#054581] mb-2">Available Groups</h4>
                          <div className="border border-[#1086a4] rounded-md divide-y divide-[#1086a4] max-h-[200px] overflow-y-auto">
                            {availableGroups.length > 0 ? (
                              getFilteredItems(availableGroups, 'groups', false).map(group => (
                                  <div
                                    key={group._id}
                                    className="flex items-center justify-between p-2 hover:bg-[#f9fafb] transition-colors"
                                  >
                                    <span className="text-sm text-gray-900">{group.title}</span>
                                    {!isView && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setIncludedGroups([...includedGroups, group]);
                                          setAvailableGroups(availableGroups.filter(g => g._id !== group._id));
                                        }}
                                        className="ml-2 text-green-500 hover:text-green-700 p-1 rounded-full hover:bg-green-50 transition-colors"
                                      >
                                        <Plus className="h-4 w-4" />
                                      </button>
                                    )}
                                  </div>
                                ))
                            ) : (
                              <div className="p-4 text-center text-gray-500 text-sm">
                                No groups available
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Questions Section with Drag and Drop */}
                <div className="border border-[#1086a4] rounded-md overflow-hidden">
                  <button
                    type="button"
                    className="w-full px-4 py-3 bg-[#f9fafb] flex items-center justify-between text-[#054581] hover:bg-gray-50 transition-colors"
                    onClick={() => setExpandedSection(expandedSection === 'questions' ? null : 'questions')}
                  >
                    <span className="font-medium">Questions ({includedQuestions.length})</span>
                    {expandedSection === 'questions' ? (
                      <ChevronUp className="h-5 w-5" />
                    ) : (
                      <ChevronDown className="h-5 w-5" />
                    )}
                  </button>
                  
                  {expandedSection === 'questions' && (
                    <div className="p-4">
                      <div className="mb-4">
                        <div className="relative flex">
                          {/* Search Context Dropdown Button */}
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() => toggleSearchDropdown('questions')}
                              className="h-full px-3 py-2 bg-[#1086a4] text-white rounded-l-md border-r border-white/20 hover:bg-[#054581] transition-colors flex items-center gap-1 focus:outline-none focus:ring-2 focus:ring-[#1086a4]"
                              disabled={isView || isSubmitting}
                            >
                              <Search className="h-5 w-5" />
                              <ChevronDown className="h-4 w-4" />
                            </button>
                            
                            {/* Dropdown Menu */}
                            {isSearchDropdownOpen.questions && (
                              <div className="absolute z-50 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200">
                                <div className="py-1">
                                  <button
                                    type="button"
                                    onClick={() => handleSearchContextChange('questions', 'all')}
                                    className={`w-full text-left px-4 py-2 text-sm ${
                                      searchContext.questions === 'all'
                                        ? 'bg-[#1086a4] text-white'
                                        : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                                  >
                                    All Questions
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleSearchContextChange('questions', 'included')}
                                    className={`w-full text-left px-4 py-2 text-sm ${
                                      searchContext.questions === 'included'
                                        ? 'bg-[#1086a4] text-white'
                                        : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                                  >
                                    Selected Questions
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleSearchContextChange('questions', 'available')}
                                    className={`w-full text-left px-4 py-2 text-sm ${
                                      searchContext.questions === 'available'
                                        ? 'bg-[#1086a4] text-white'
                                        : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                                  >
                                    Available Questions
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Search Input */}
                          <div className="flex-1 relative">
                            <input
                              type="text"
                              className="w-full pl-3 pr-10 py-2 border border-l-0 border-[#1086a4] rounded-r-md bg-[#f9fafb] text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors"
                              placeholder={getSearchPlaceholder('questions')}
                              value={searchQuery.questions}
                              onChange={(e) => setSearchQuery({ ...searchQuery, questions: e.target.value })}
                              disabled={isView || isSubmitting}
                            />
                            {searchQuery.questions && (
                              <button
                                type="button"
                                onClick={() => setSearchQuery(prev => ({ ...prev, questions: '' }))}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        {/* Selected Questions with Drag and Drop */}
                        <div className={searchContext.questions === 'available' ? 'opacity-50' : ''}>
                          <h4 className="text-sm font-medium text-[#054581] mb-2">
                            Selected Questions {includedQuestions.length > 0 && !isView && (
                              <span className="text-xs text-gray-500 font-normal ml-2">
                                (Drag to reorder)
                              </span>
                            )}
                          </h4>
                          <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleQuestionDragEnd}
                          >
                            <SortableContext
                              items={getFilteredItems(includedQuestions, 'questions', true).map(q => q._id)}
                              strategy={verticalListSortingStrategy}
                            >
                              <div className="border border-[#1086a4] rounded-md max-h-[200px] overflow-y-auto">
                                {includedQuestions.length > 0 ? (
                                  getFilteredItems(includedQuestions, 'questions', true).map((question) => (
                                    <SortableQuestionItem
                                      key={question._id}
                                      question={question}
                                      onRemove={(id) => {
                                        setIncludedQuestions(includedQuestions.filter(q => q._id !== id));
                                        setAvailableQuestions([...availableQuestions, question]);
                                      }}
                                    />
                                  ))
                                ) : (
                                  <div className="p-4 text-center text-gray-500 text-sm">
                                    No questions selected
                                  </div>
                                )}
                              </div>
                            </SortableContext>
                          </DndContext>
                        </div>

                        {/* Available Questions */}
                        <div className={searchContext.questions === 'included' ? 'opacity-50' : ''}>
                          <h4 className="text-sm font-medium text-[#054581] mb-2">Available Questions</h4>
                          <div className="border border-[#1086a4] rounded-md divide-y divide-[#1086a4] max-h-[200px] overflow-y-auto">
                            {availableQuestions.length > 0 ? (
                              getFilteredItems(availableQuestions, 'questions', false).map(question => (
                                  <div
                                    key={question._id}
                                    className="flex items-center justify-between p-2 hover:bg-[#f9fafb] transition-colors"
                                  >
                                    <span className="text-sm text-gray-900">{question.text}</span>
                                    {!isView && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setIncludedQuestions([...includedQuestions, question]);
                                          setAvailableQuestions(availableQuestions.filter(q => q._id !== question._id));
                                        }}
                                        className="ml-2 text-green-500 hover:text-green-700 p-1 rounded-full hover:bg-green-50 transition-colors"
                                      >
                                        <Plus className="h-4 w-4" />
                                      </button>
                                    )}
                                  </div>
                                ))
                            ) : (
                              <div className="p-4 text-center text-gray-500 text-sm">
                                No questions available
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Repeatable Submission Forms Section */}
                <div className="border border-[#1086a4] rounded-md overflow-hidden">
                  <button
                    type="button"
                    className="w-full px-4 py-3 bg-[#f9fafb] flex items-center justify-between text-[#054581] hover:bg-gray-50 transition-colors"
                    onClick={() => setExpandedSection(expandedSection === 'submissionForms' ? null : 'submissionForms')}
                  >
                    <span className="font-medium">Repeatable Submission Forms ({includedSubmissionForms.length})</span>
                    {expandedSection === 'submissionForms' ? (
                      <ChevronUp className="h-5 w-5" />
                    ) : (
                      <ChevronDown className="h-5 w-5" />
                    )}
                  </button>
                  
                  {expandedSection === 'submissionForms' && (
                    <div className="p-4">
                      <div className="mb-4">
                        <div className="relative flex">
                          {/* Search Context Dropdown Button */}
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() => toggleSearchDropdown('submissionForms')}
                              className="h-full px-3 py-2 bg-[#1086a4] text-white rounded-l-md border-r border-white/20 hover:bg-[#054581] transition-colors flex items-center gap-1 focus:outline-none focus:ring-2 focus:ring-[#1086a4]"
                              disabled={isView || isSubmitting}
                            >
                              <Search className="h-5 w-5" />
                              <ChevronDown className="h-4 w-4" />
                            </button>
                            
                            {/* Dropdown Menu */}
                            {isSearchDropdownOpen.submissionForms && (
                              <div className="absolute z-50 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200">
                                <div className="py-1">
                                  <button
                                    type="button"
                                    onClick={() => handleSearchContextChange('submissionForms', 'all')}
                                    className={`w-full text-left px-4 py-2 text-sm ${
                                      searchContext.submissionForms === 'all'
                                        ? 'bg-[#1086a4] text-white'
                                        : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                                  >
                                    All Submission Forms
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleSearchContextChange('submissionForms', 'included')}
                                    className={`w-full text-left px-4 py-2 text-sm ${
                                      searchContext.submissionForms === 'included'
                                        ? 'bg-[#1086a4] text-white'
                                        : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                                  >
                                    Selected Submission Forms
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleSearchContextChange('submissionForms', 'available')}
                                    className={`w-full text-left px-4 py-2 text-sm ${
                                      searchContext.submissionForms === 'available'
                                        ? 'bg-[#1086a4] text-white'
                                        : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                                  >
                                    Available Submission Forms
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Search Input */}
                          <div className="flex-1 relative">
                            <input
                              type="text"
                              className="w-full pl-3 pr-10 py-2 border border-l-0 border-[#1086a4] rounded-r-md bg-[#f9fafb] text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors"
                              placeholder={getSearchPlaceholder('submissionForms')}
                              value={searchQuery.submissionForms}
                              onChange={(e) => setSearchQuery({ ...searchQuery, submissionForms: e.target.value })}
                              disabled={isView || isSubmitting}
                            />
                            {searchQuery.submissionForms && (
                              <button
                                type="button"
                                onClick={() => setSearchQuery(prev => ({ ...prev, submissionForms: '' }))}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="space-y-4">
                        {/* Selected Submission Forms */}
                        <div className={searchContext.submissionForms === 'available' ? 'opacity-50' : ''}>
                          <h4 className="text-sm font-medium text-[#054581] mb-2">Selected Submission Forms</h4>
                          <div className="border border-[#1086a4] rounded-md divide-y divide-[#1086a4] max-h-[200px] overflow-y-auto">
                            {includedSubmissionForms.length > 0 ? (
                              getFilteredItems(includedSubmissionForms, 'submissionForms', true).map(sf => (
                                <div
                                  key={sf._id}
                                  className="flex items-center justify-between p-2 hover:bg-[#f9fafb] transition-colors"
                                >
                                  <span className="text-sm text-gray-900">{sf.title}</span>
                                  {!isView && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setIncludedSubmissionForms(includedSubmissionForms.filter(f => f._id !== sf._id));
                                        setAvailableSubmissionForms([...availableSubmissionForms, sf]);
                                      }}
                                      className="ml-2 text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 transition-colors"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                              ))
                            ) : (
                              <div className="p-4 text-center text-gray-500 text-sm">
                                No submission forms selected
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Available Submission Forms */}
                        <div className={searchContext.submissionForms === 'included' ? 'opacity-50' : ''}>
                          <h4 className="text-sm font-medium text-[#054581] mb-2">Available Submission Forms</h4>
                          <div className="border border-[#1086a4] rounded-md divide-y divide-[#1086a4] max-h-[200px] overflow-y-auto">
                            {availableSubmissionForms.length > 0 ? (
                              getFilteredItems(availableSubmissionForms, 'submissionForms', false).map(sf => (
                                <div
                                  key={sf._id}
                                  className="flex items-center justify-between p-2 hover:bg-[#f9fafb] transition-colors"
                                >
                                  <span className="text-sm text-gray-900">{sf.title}</span>
                                  {!isView && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setIncludedSubmissionForms([...includedSubmissionForms, sf]);
                                        setAvailableSubmissionForms(availableSubmissionForms.filter(f => f._id !== sf._id));
                                      }}
                                      className="ml-2 text-green-500 hover:text-green-700 p-1 rounded-full hover:bg-green-50 transition-colors"
                                    >
                                      <Plus className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                              ))
                            ) : (
                              <div className="p-4 text-center text-gray-500 text-sm">
                                No submission forms available
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {(isNew || isEdit) && (
          <div className="border-t border-[#1086a4] p-6">
            <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end">
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:w-auto px-4 py-2 border border-[#1086a4] rounded-md text-[#1086a4] font-medium hover:bg-[#f9fafb] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="w-full sm:w-auto px-4 py-2 bg-[#1086a4] text-white rounded-md font-medium hover:bg-[#054581] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {isNew ? 'Adding Program...' : 'Saving Changes...'}
                  </>
                ) : (
                  isNew ? 'Add Program' : 'Save Changes'
                )}
              </button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default ProgramForm;
